import { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '../context/WalletContext';
import { useNFT } from '../context/NFTContext';
import { getAccessLevel, canPurchaseLP, validateNftPurchase } from '../services/userAccessService';

/**
 * Hook para gestionar el nivel de acceso del usuario
 * 
 * Este hook:
 * - Consulta el nivel de acceso del backend
 * - Cachea el resultado para evitar llamadas excesivas
 * - Se actualiza automáticamente cuando cambia la wallet o se compra NFT
 * - Proporciona métodos para verificar permisos específicos
 * 
 * @returns {Object} Estado y métodos de acceso
 */
export const useAccessLevel = () => {
  const { isConnected, address } = useWallet();
  const { hasNFTs, selectedNFT } = useNFT();
  
  const [accessInfo, setAccessInfo] = useState({
    hasNFT: false,
    accessLevel: 'limited',
    canPurchaseLP: false,
    requiresSponsorAtPurchase: false,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache para evitar llamadas repetidas
  const cacheRef = useRef({
    data: null,
    timestamp: null,
    address: null,
  });
  
  const CACHE_DURATION = 30000; // 30 segundos de cache
  
  /**
   * Carga el nivel de acceso del backend
   */
  const loadAccessLevel = useCallback(async (forceRefresh = false) => {
    // Si no hay wallet conectada, resetear estado
    if (!isConnected || !address) {
      setAccessInfo({
        hasNFT: false,
        accessLevel: 'limited',
        canPurchaseLP: false,
        requiresSponsorAtPurchase: false,
      });
      setIsLoading(false);
      setError(null);
      cacheRef.current = { data: null, timestamp: null, address: null };
      return;
    }
    
    // Verificar cache
    const cache = cacheRef.current;
    const now = Date.now();
    const isCacheValid = 
      cache.data &&
      cache.address === address.toLowerCase() &&
      cache.timestamp &&
      (now - cache.timestamp) < CACHE_DURATION;
    
    if (isCacheValid && !forceRefresh) {
      setAccessInfo(cache.data);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getAccessLevel();
      
      if (result.success) {
        const accessData = result.data;
        
        // Actualizar cache
        cacheRef.current = {
          data: accessData,
          timestamp: now,
          address: address.toLowerCase(),
        };
        
        setAccessInfo(accessData);
      } else {
        // Si hay error pero tenemos datos en cache, mantenerlos
        if (cache.data && cache.address === address.toLowerCase()) {
          setAccessInfo(cache.data);
        } else {
          // Si no hay cache, usar valores por defecto
          setAccessInfo({
            hasNFT: false,
            accessLevel: 'limited',
            canPurchaseLP: false,
            requiresSponsorAtPurchase: false,
          });
        }
        
        setError(result.error || 'Error al obtener nivel de acceso');
      }
    } catch (err) {
      console.error('[useAccessLevel] Error:', err);
      
      // Si hay error pero tenemos datos en cache, mantenerlos
      if (cache.data && cache.address === address.toLowerCase()) {
        setAccessInfo(cache.data);
      } else {
        setAccessInfo({
          hasNFT: false,
          accessLevel: 'limited',
          canPurchaseLP: false,
          requiresSponsorAtPurchase: false,
        });
      }
      
      setError(err.message || 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, address]);
  
  /**
   * Verifica si puede comprar LP (llamada directa al backend)
   */
  const checkCanPurchaseLP = useCallback(async () => {
    if (!isConnected || !address) {
      return {
        canPurchaseLP: false,
        hasNFT: false,
        message: 'You must connect your wallet first.',
      };
    }
    
    try {
      const result = await canPurchaseLP();
      return result;
    } catch (err) {
      console.error('[useAccessLevel] Error verificando compra LP:', err);
      return {
        canPurchaseLP: false,
        hasNFT: false,
        message: 'Error verifying LP purchase eligibility.',
      };
    }
  }, [isConnected, address]);
  
  /**
   * Valida requisitos para comprar NFT (llamada directa al backend)
   */
  const checkNftPurchaseValidation = useCallback(async () => {
    if (!isConnected || !address) {
      return {
        requiresSponsorAtPurchase: false,
        hasNFT: false,
        message: 'You must connect your wallet first.',
      };
    }
    
    try {
      const result = await validateNftPurchase();
      return result;
    } catch (err) {
      console.error('[useAccessLevel] Error validando compra NFT:', err);
      return {
        requiresSponsorAtPurchase: false,
        hasNFT: false,
        message: 'Error validating NFT purchase requirements.',
      };
    }
  }, [isConnected, address]);
  
  // Cargar nivel de acceso cuando cambia la wallet
  useEffect(() => {
    loadAccessLevel();
  }, [loadAccessLevel]);
  
  // Invalidar cache cuando cambia el estado de NFT (puede haber comprado uno)
  useEffect(() => {
    if (hasNFTs !== accessInfo.hasNFT) {
      loadAccessLevel(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNFTs, selectedNFT]);
  
  // Invalidar cache cuando cambia la wallet
  useEffect(() => {
    if (address && cacheRef.current.address !== address.toLowerCase()) {
      cacheRef.current = { data: null, timestamp: null, address: null };
    }
  }, [address]);
  
  return {
    // Estado de acceso
    hasNFT: accessInfo.hasNFT,
    accessLevel: accessInfo.accessLevel,
    canPurchaseLP: accessInfo.canPurchaseLP,
    requiresSponsorAtPurchase: accessInfo.requiresSponsorAtPurchase,
    
    // Estado de carga
    isLoading,
    error,
    
    // Métodos
    refresh: () => loadAccessLevel(true),
    checkCanPurchaseLP,
    checkNftPurchaseValidation,
    
    // Helpers
    isFullAccess: accessInfo.accessLevel === 'full',
    isLimitedAccess: accessInfo.accessLevel === 'limited',
  };
};

export default useAccessLevel;
